// frontend/src/App.js
import React from 'react';
// Import necessary components from react-router-dom
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';

// --- Import Page/Layout Components ---
import Dashboard from './components/Dashboard';     // Main dashboard page for authenticated users
import Layout from './components/Layout';         // Shared layout component (header, footer, etc.)
import JoinDiscord from './components/JoinDiscord'; // Page shown for role check failure
import ErrorPage from './components/ErrorPage';     // Generic error page
import BoostHistoryPage from './pages/BoostHistory';
import ServerLogsPage from './pages/ServerLogs';

/**
 * Renders the Login Page within the shared application Layout.
 * Displays the Exodus banner and handles showing login error messages.
 * @returns {React.ReactElement} The rendered Login Page component.
 */
const LoginPage = () => {
  // Determine if a login error occurred based on URL query parameters
  const showError = window.location.search.includes('error');

  return (
    // Use the shared Layout component. No 'user' prop is passed,
    // indicating the user is not logged in (Layout shows Login button).
    <Layout>
      {/* Container for login page specific content */}
      {/* Uses flexbox to center content vertically and horizontally within the Layout's main area. */}
      {/* Removed min-h calculation and h-full to prevent overflow issues. */}
      <div className="relative z-20 flex flex-col items-center justify-center text-center px-4"> {/* Ensure no h-full or min-h-[calc(...)] here */}

        {/* Exodus Community Banner Image */}
        <img
            src="/ExodusBanner.png" // Assumes image is located in the /public folder
            alt="Exodus Community Banner"
            // Apply styling: 60% width, max large size, auto height, centered, rounded, shadow, margin bottom
            className="w-3/5 max-w-lg h-auto mx-auto rounded-lg shadow-lg mb-8"
            onError={(e) => {
                // Fallback logic if the primary banner image fails to load
                e.target.onerror = null; // Prevent potential infinite loop on placeholder error
                e.target.src = "https://placehold.co/600x300/777/eee?text=Banner+Not+Found"; // Display a placeholder
                e.target.alt = "Error loading banner";
            }}
        />

        {/* Conditionally render error message if login failed */}
        {showError && (
          <p className="text-red-400 mt-4 inline-block bg-black bg-opacity-50 px-4 py-2 rounded">
              Login failed. Please ensure you are a member with the correct role.
          </p>
        )}
        {/* Placeholder comment for potential future additions */}
        {/* <p className="mt-4">Please log in using the button in the header.</p> */}
      </div>
    </Layout>
  );
};

/**
 * Main application component.
 * Sets up the React Router and defines the application's routes,
 * mapping URL paths to specific page components.
 * @returns {React.ReactElement} The main application router setup.
 */
function App() {
  return (
    // BrowserRouter provides routing capabilities to the application
    <Router>
      {/* Routes component manages the collection of individual Route definitions */}
      <Routes>
        {/* Route definition for the authenticated user dashboard */}
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Route definition for the login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* Route definition for the page shown when Discord role check fails */}
        <Route path="/join-discord" element={<JoinDiscord />} />

        {/* Route definition for the generic server error page */}
        <Route path="/error" element={<ErrorPage />} />

        {/* Route definition for the Admin Boost History page */}
        <Route path="/admin/boost-history" element={<BoostHistoryPage />} />

        {/* Route definition for the Admin Server Logs page */}
        <Route path="/admin/logs" element={<ServerLogsPage />} />        

        {/* Default route for the root path ('/') - Redirects immediately to the login page */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Optional: Catch-all route for handling 404 Not Found pages */}
        {/* <Route path="*" element={<div><h2>404 Not Found</h2></div>} /> */}
      </Routes>
    </Router>
  );
}

export default App;
